// The whole file sits in an IIFE because the loading script tag lives in the
// header section: the theme editor re-executes it on every section re-render,
// and a bare top-level class binding would throw "already been declared" and
// silently kill the file the second time around.
(() => {
class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.panel = this.querySelector('#cart-notification');
    this.items = this.querySelector('[data-h7-cart-items]');
    this.empty = this.querySelector('[data-h7-cart-empty]');
    this.shipping = this.querySelector('[data-h7-cart-shipping]');
    this.shippingCopy = this.querySelector('[data-h7-cart-shipping-copy]');
    this.shippingSecondary = this.querySelector('[data-h7-cart-shipping-secondary]');
    this.shippingTrack = this.querySelector('[data-h7-cart-shipping-track]');
    this.footer = this.querySelector('[data-h7-cart-footer]');
    this.status = this.querySelector('[data-h7-cart-status]');
    this.count = this.querySelector('[data-h7-cart-count]');
    this.total = this.querySelector('[data-h7-cart-total]');
    this.originalSubtotalRow = this.querySelector('[data-h7-original-subtotal-row]');
    this.originalSubtotal = this.querySelector('[data-h7-original-subtotal]');
    this.savings = this.querySelector('[data-h7-cart-savings]');
    this.productSavingRow = this.querySelector('[data-h7-product-saving-row]');
    this.productSaving = this.querySelector('[data-h7-product-saving]');
    this.saleSavingRow = this.querySelector('[data-h7-sale-saving-row]');
    this.saleSaving = this.querySelector('[data-h7-sale-saving]');
    this.checkout = this.querySelector('[data-h7-cart-checkout]');
    this.closeButtons = this.querySelectorAll('[data-h7-cart-close]');
    this.ownerSection = this.closest('.shopify-section');
    this.copy = {
      loading: this.dataset.cartLoading || 'Loading...',
      cartError: this.dataset.cartError || 'There was an error while updating your cart. Please try again.',
      quantity: this.dataset.quantityLabel || 'Quantity for {product}',
      decrease: this.dataset.quantityDecrease || 'Decrease quantity for {product}',
      increase: this.dataset.quantityIncrease || 'Increase quantity for {product}',
      remove: this.dataset.removeLabel || 'Remove {product}',
      discount: this.dataset.discountLabel || 'Discount',
      shippingUk: this.dataset.shippingUk || 'FREE UK delivery',
      shippingUkSecondary: this.dataset.shippingUkSecondary || '',
      shippingCheckout: this.dataset.shippingCheckout || 'Rates and delivery estimates confirmed at checkout.',
      subscriptionOption: this.dataset.subscriptionOptionLabel || 'subscribe & save',
    };
    // Absent attribute means the sale is off, which must read as "no reference
    // price" rather than as 0% off.
    const salePercent = Number(this.dataset.salePercent);
    this.salePercent = Number.isFinite(salePercent) && salePercent > 0 && salePercent < 100 ? salePercent : 0;
    this.onKeyUp = this.handleKeyUp.bind(this);
    this.onClick = this.handleClick.bind(this);

    this.pending = false;
    // Monotonic counter for /cart/change.js requests: only the newest response
    // may paint, so two overlapping quantity taps cannot leave the panel
    // showing the state of whichever request happened to land last.
    this.changeRequestId = 0;

    // Any component that mutates the cart publishes the result. Feeding that
    // into the shared state keeps the snapshot we open from honest, so a
    // porch-theft toggle or a header change can't leave us painting a stale
    // cart on the next open.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.cartData?.items) window.H7CartState?.set(event.cartData);
        else window.H7CartState?.invalidate();
      });
    }

    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    this.panel?.addEventListener('keyup', this.onKeyUp);
    this.panel?.addEventListener('click', this.onClick);
    if (this.ownerSection) {
      this.onOwnerSectionUnload = (event) => {
        if (event.target !== this.ownerSection) return;
        document.removeEventListener('shopify:section:unload', this.onOwnerSectionUnload);
        this.cartUpdateUnsubscriber?.();
        this.close();
        this.remove();
      };
      document.addEventListener('shopify:section:unload', this.onOwnerSectionUnload);
    }
  }

  // The sidebar paints itself from cart JSON. Asking Shopify to render cart
  // sections during /cart/add costs ~100ms of server time for markup we would
  // immediately overwrite, so product-form skips it.
  get rendersSectionsOnAdd() {
    return false;
  }

  async open(refresh = true) {
    if (this.parentElement !== document.body) document.body.append(this);
    const cart = refresh ? await this.refresh() : null;
    // Same race as renderContents: a shopper who closed the panel while the
    // add was in flight must not have it reopened by the late response.
    // Only openPending() clears the flag, because that is the point where the
    // shopper actually asked for the panel.
    if (this.dismissed) return;
    this.reveal();
    this.dispatchCartViewEvent();
  }

  /**
   * Open on the click, not on the response. We paint the cart we already know
   * about plus a placeholder for the line being added, so the panel is on
   * screen in the same frame as the tap and the network happens behind it.
   */
  openPending() {
    if (this.parentElement !== document.body) document.body.append(this);

    this.dismissed = false;
    this.pending = true;
    const cart = window.H7CartState?.peek();
    if (cart) this.render(cart);
    else this.clearStatus();
    this.showPendingLine();
    this.reveal();
  }

  /** The add failed: drop the placeholder and say so inside the panel. */
  cancelPending(message) {
    this.pending = false;
    this.classList.remove('is-pending');
    this.querySelector('[data-h7-pending-line]')?.remove();
    const cart = window.H7CartState?.peek();
    if (cart) this.render(cart);
    if (message) this.setStatus(message);
  }

  showPendingLine() {
    if (!this.items || this.querySelector('[data-h7-pending-line]')) return;

    this.classList.add('is-pending');
    this.empty.hidden = true;
    const row = document.createElement('div');
    row.className = 'h7-cart-sidebar__item h7-cart-sidebar__item--pending';
    row.dataset.h7PendingLine = '';
    row.setAttribute('aria-hidden', 'true');
    row.innerHTML =
      '<span class="h7-cart-sidebar__pending-image"></span>' +
      '<div class="h7-cart-sidebar__pending-details">' +
      '<span class="h7-cart-sidebar__pending-bar"></span>' +
      '<span class="h7-cart-sidebar__pending-bar h7-cart-sidebar__pending-bar--short"></span>' +
      '<span class="h7-cart-sidebar__pending-bar h7-cart-sidebar__pending-bar--controls"></span>' +
      '</div>' +
      '<span class="h7-cart-sidebar__pending-bar h7-cart-sidebar__pending-bar--price"></span>';
    this.items.append(row);
    this.setStatus(this.copy.loading);
  }

  reveal() {
    this.dismissed = false;
    this.classList.add('active');
    document.documentElement.classList.add('h7-cart-sidebar-open');
    this.panel?.focus({ preventScroll: true });
    if (typeof trapFocus === 'function' && this.panel) trapFocus(this.panel);
  }

  close() {
    // Remember the dismissal: the add request is often still in flight, and
    // its response used to re-reveal the panel a few hundred ms after the
    // shopper closed it, re-locking page scroll behind their back.
    this.dismissed = true;
    this.classList.remove('active');
    document.documentElement.classList.remove('h7-cart-sidebar-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    this.activeElement?.focus?.({ preventScroll: true });
  }

  handleKeyUp(event) {
    if (event.code === 'Escape') this.close();
  }

  handleClick(event) {
    const quantityButton = event.target.closest('[data-h7-cart-line][data-h7-cart-quantity]');
    if (quantityButton) {
      this.changeQuantity(quantityButton);
      return;
    }

    const removeButton = event.target.closest('[data-h7-cart-remove]');
    if (removeButton) this.changeQuantity(removeButton);
  }

  /**
   * @param {{silent?: boolean}} options silent skips the loading and error
   *   copy, for a reconcile happening behind an already-correct panel.
   */
  async refresh({ silent = false } = {}) {
    if (!this.pending && !silent) this.setStatus(this.copy.loading);

    try {
      const cart = window.H7CartState
        ? await window.H7CartState.get()
        : await fetch(this.dataset.cartJsonUrl, { headers: { Accept: 'application/json' } }).then((response) => {
            if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
            return response.json();
          });
      this.pending = false;
      this.classList.remove('is-pending');
      this.render(cart);
      return cart;
    } catch (error) {
      this.pending = false;
      this.classList.remove('is-pending');
      // A failed reconcile leaves correct line prices on screen, so shouting
      // about it would be worse than staying quiet.
      if (!silent) this.setStatus(this.copy.cartError);
      return null;
    }
  }

  render(cart) {
    this.clearStatus();
    this.items.replaceChildren();
    this.count.textContent = cart.item_count;
    const renderableItems = Array.isArray(cart.items)
      ? cart.items.filter((item) => Number(item.quantity) > 0)
      : [];

    if (Number(cart.item_count) < 1 || !renderableItems.length) {
      this.classList.remove('is-estimated');
      this.empty.hidden = false;
      if (this.shipping) this.shipping.hidden = true;
      this.footer.hidden = true;
      return;
    }

    renderableItems.forEach((item, index) => this.items.append(this.createItem(item, index, cart.currency)));
    this.empty.hidden = true;
    this.renderShipping();
    this.footer.hidden = false;

    const cartOriginal = Number(cart.original_total_price || cart.total_price);
    const productSaving = Math.max(0, cartOriginal - Number(cart.total_price));
    // Same base as snippets/h7-cart-value-summary.liquid: derive every
    // cart-level reference figure from the original total so the sale row, the
    // struck subtotal and the savings total always reconcile.
    const saleReference = this.saleComparePrice(cartOriginal);
    const saleSaving = saleReference ? saleReference - cartOriginal : 0;
    const originalSubtotal = saleReference || cartOriginal;
    const totalValue = productSaving + saleSaving;

    this.total.textContent = this.formatMoney(cart.total_price, cart.currency);
    this.originalSubtotalRow.hidden = originalSubtotal <= Number(cart.total_price);
    this.originalSubtotal.textContent = this.formatMoney(originalSubtotal, cart.currency);
    if (this.saleSavingRow && this.saleSaving) {
      this.saleSavingRow.hidden = saleSaving < 1;
      this.saleSaving.textContent = saleSaving > 0 ? this.formatMoney(saleSaving, cart.currency) : '';
    }
    this.productSavingRow.hidden = productSaving < 1;
    this.productSaving.textContent = productSaving > 0 ? this.formatMoney(productSaving, cart.currency) : '';
    this.savings.textContent = this.formatMoney(totalValue, cart.currency);
    this.classList.toggle('is-estimated', Boolean(cart.estimated));
  }

  renderShipping() {
    if (!this.shipping || !this.shippingCopy) return;

    const market = this.dataset.shippingMarket || 'unsupported';
    const ukVerified = this.dataset.ukShippingVerified === 'true';

    let primary = '';
    let secondary = '';
    let progress = 0;

    if (market === 'uk' && ukVerified) {
      primary = this.copy.shippingUk;
      secondary = this.copy.shippingUkSecondary;
      progress = 100;
    } else {
      primary = this.copy.shippingCheckout;
    }

    this.shipping.hidden = !primary;
    if (!primary) return;

    this.shippingCopy.textContent = primary;
    if (this.shippingSecondary) {
      this.shippingSecondary.textContent = secondary;
      this.shippingSecondary.hidden = !secondary;
    }
    if (this.shippingTrack) {
      this.shippingTrack.hidden = progress < 1;
      this.shippingTrack.setAttribute('aria-valuenow', String(progress));
      this.shippingTrack.style.setProperty('--h7-shipping-progress', `${progress}%`);
    }
  }

  createItem(item, index, currency) {
    const row = document.createElement('article');
    row.className = 'h7-cart-sidebar__item';

    const isDeliveryGuarantee = item.handle === 'hour-seven-delivery-guarantee';
    const imageLink = document.createElement(isDeliveryGuarantee ? 'span' : 'a');
    imageLink.className = 'h7-cart-sidebar__item-image';
    if (!isDeliveryGuarantee) {
      imageLink.href = item.url;
      imageLink.tabIndex = -1;
      imageLink.setAttribute('aria-hidden', 'true');
    }
    if (item.image) {
      const image = document.createElement('img');
      image.src = this.withImageWidth(item.image, 220);
      image.alt = '';
      image.width = 88;
      image.height = 88;
      image.loading = 'lazy';
      imageLink.append(image);
    }

    const details = document.createElement('div');
    details.className = 'h7-cart-sidebar__item-details';

    const title = document.createElement(isDeliveryGuarantee ? 'span' : 'a');
    title.className = 'h7-cart-sidebar__item-title';
    if (!isDeliveryGuarantee) title.href = item.url;
    title.textContent = item.product_title;
    details.append(title);

    const metadata = [
      item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '',
      item.selling_plan_allocation ? this.copy.subscriptionOption : '',
    ].filter(Boolean);
    metadata.forEach((value) => {
      const line = document.createElement('p');
      line.className = 'h7-cart-sidebar__item-meta';
      line.textContent = value;
      details.append(line);
    });

    const controls = document.createElement('div');
    controls.className = 'h7-cart-sidebar__item-controls';
    controls.setAttribute('aria-label', this.formatCopy(this.copy.quantity, item.product_title));
    controls.append(
      this.createQuantityButton('−', item, Math.max(0, item.quantity - 1), this.formatCopy(this.copy.decrease, item.product_title)),
      this.createQuantityValue(item.quantity),
      this.createQuantityButton('+', item, item.quantity + 1, this.formatCopy(this.copy.increase, item.product_title))
    );
    details.append(controls);

    const price = document.createElement('div');
    price.className = 'h7-cart-sidebar__item-price';
    const lineOriginal = Number(item.original_line_price || item.final_line_price || 0);
    const lineReference = this.saleComparePrice(lineOriginal) || lineOriginal;
    const itemSavings = Math.max(0, lineReference - Number(item.final_line_price || 0));
    if (itemSavings > 0) {
      const original = document.createElement('s');
      original.textContent = this.formatMoney(lineReference, currency);
      price.append(original);
    }
    const finalPrice = document.createElement('strong');
    finalPrice.textContent = this.formatMoney(item.final_line_price, currency);
    price.append(finalPrice);
    if (itemSavings > 0) {
      const saved = document.createElement('em');
      saved.textContent = `(${this.copy.discount} ${this.formatMoney(itemSavings, currency)})`;
      price.append(saved);
    }

    const remove = document.createElement('button');
    remove.className = 'h7-cart-sidebar__remove';
    remove.type = 'button';
    remove.dataset.h7CartRemove = '';
    remove.dataset.h7CartLine = item.key;
    remove.dataset.h7CartQuantity = '0';
    remove.dataset.h7ProductHandle = item.handle || '';
    remove.dataset.h7VariantId = item.variant_id || '';
    remove.dataset.h7SellingPlanId = item.selling_plan_allocation?.selling_plan?.id || '';
    remove.setAttribute('aria-label', this.formatCopy(this.copy.remove, item.product_title));
    remove.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M6 6.5v8m4-8v8m4-8v8M4 4.5h12M7 4.5l.7-2h4.6l.7 2M5.5 4.5l.7 12h7.6l.7-12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    row.append(imageLink, details, price, remove);
    return row;
  }

  createQuantityButton(label, item, quantity, accessibleLabel) {
    const button = document.createElement('button');
    button.className = 'h7-cart-sidebar__quantity-button';
    button.type = 'button';
    button.textContent = label;
    button.dataset.h7CartLine = item.key;
    button.dataset.h7CartQuantity = quantity;
    button.dataset.h7ProductHandle = item.handle || '';
    button.dataset.h7VariantId = item.variant_id || '';
    button.dataset.h7SellingPlanId = item.selling_plan_allocation?.selling_plan?.id || '';
    button.setAttribute('aria-label', accessibleLabel);
    return button;
  }

  createQuantityValue(quantity) {
    const value = document.createElement('span');
    value.className = 'h7-cart-sidebar__quantity';
    value.textContent = quantity;
    value.setAttribute('aria-live', 'polite');
    return value;
  }

  async changeQuantity(button) {
    if (button.disabled) return;
    button.disabled = true;
    this.classList.add('is-updating');

    try {
      const item = {
        key: button.dataset.h7CartLine,
        handle: button.dataset.h7ProductHandle,
        variantId: button.dataset.h7VariantId,
        sellingPlanId: button.dataset.h7SellingPlanId,
      };
      await this.commitQuantity(item, Number(button.dataset.h7CartQuantity));
    } catch (error) {
      // Shopify's 422 body carries the actual reason ("You can only add N of
      // this item"); only unexplained failures get the generic copy.
      this.setStatus(error?.isCartError && error.message ? error.message : this.copy.cartError);
    } finally {
      this.classList.remove('is-updating');
      button.disabled = false;
    }
  }

  async commitQuantity(item, quantity) {
    const requestId = ++this.changeRequestId;
    const response = await fetch(this.dataset.cartChangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: Number(quantity) }),
    });
    const cart = await response.json();
    if (!response.ok) {
      const error = new Error(cart.description || 'Cart update failed');
      error.isCartError = Boolean(cart.description);
      throw error;
    }
    // A newer change is already in flight; its response owns the panel.
    if (requestId !== this.changeRequestId) return cart;
    window.H7CartState?.set(cart);
    this.render(cart);
    this.publishCartUpdate(cart, 'h7-cart-sidebar');
    return cart;
  }

  setStatus(message) {
    if (!this.status) return;
    this.status.hidden = false;
    this.status.textContent = message;
  }

  clearStatus() {
    if (!this.status) return;
    this.status.hidden = true;
    this.status.textContent = '';
  }

  async renderContents(parsedState) {
    // The add response is the resulting line, so folding it into the state we
    // already hold gives us the real title, image and line prices without a
    // second round trip. Only order-level discounts need Shopify to price
    // them, and render() marks the total as estimated when that applies.
    const merged = window.H7CartState?.mergeLine(parsedState);
    if (merged) {
      this.pending = false;
      this.classList.remove('is-pending');
      this.render(merged);
      if (!this.classList.contains('active') && !this.dismissed) this.reveal();
      this.dispatchCartViewEvent();
      // Reconcile behind the open panel, never in front of it. The added line
      // is already exact; this settles order-level discounts and any line the
      // page-render snapshot could not have known about.
      if (merged.estimated || window.H7CartState?.needsReconcile()) this.refresh({ silent: true });
      return;
    }

    await this.refresh();
    await this.open(false);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  publishCartUpdate(cart, source) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData: cart });
  }

  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;
    try {
      // Analytics reuses the state the panel just rendered rather than
      // spending another round trip on data we already have.
      const cart = window.H7CartState
        ? await window.H7CartState.get({ maxAge: 5000 })
        : await fetch(this.dataset.cartJsonUrl).then((response) => response.json());
      if (!cart?.currency) return;
      this.dispatchEvent(new CartViewEvent({ context: 'dialog', cart: CartViewEvent.createCartFromAjaxResponse(cart) }));
    } catch (error) {
      // Analytics must never block the cart.
    }
  }

  /**
   * Mirrors snippets/h7-sale-price.liquid: the reference price is the one the
   * advertised percentage came off, so 20% off means price / 0.8, charmed onto
   * a .99 ending. Returns 0 when there is nothing to strike through.
   */
  saleComparePrice(price) {
    if (!this.salePercent || !(price > 0)) return 0;
    const reference = Math.round((price * 100) / (100 - this.salePercent));
    if (reference <= price) return 0;
    const charmed = Math.round((reference + 1) / 100) * 100 - 1;
    return charmed > price ? charmed : reference;
  }

  formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  }

  withImageWidth(source, width) {
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (error) {
      return source;
    }
  }

  formatCopy(template, productTitle) {
    return String(template || '').replace('{product}', productTitle || '');
  }
}

if (!customElements.get('cart-notification')) customElements.define('cart-notification', CartNotification);
})();
